import { AuthError } from '@azure/msal-browser';

import { appInsights } from '@/utils/appInsightsUtil';
import { getErrorDetails, logError } from '@/utils/errorHandlerUtil';

// Every place the app sends the user away to B2C (login, token, or logout redirect) should report
// why through trackAuthRedirect, so "I keep getting sent back to login" can be traced in App Insights:
//   customEvents | where name == 'AuthRedirect' | extend reason = tostring(customDimensions.reason)
export type AuthRedirectReason =
  // Unexpected: the user did not ask to leave
  | 'InteractionRequired' // acquireTokenSilent needs the user to sign in again
  | 'MsalTemplateError' // MsalAuthenticationTemplate reported an error and retried login
  // Expected: user action or policy
  | 'Idle' // 12h without activity in any tab
  | 'ForgotPassword'
  | 'UserSignOut'
  | 'Impersonation'
  | 'InactiveAccount';

const EXPECTED_REASONS: ReadonlySet<AuthRedirectReason> = new Set([
  'Idle',
  'ForgotPassword',
  'UserSignOut',
  'Impersonation',
  'InactiveAccount',
]);

export type AuthRedirectDetails = {
  error?: unknown;
  b2cId?: string | null;
  [key: string]: unknown;
};

type RedirectHistoryEntry = { reason: AuthRedirectReason; at: number };

const REDIRECT_HISTORY_KEY = 'rpa.authRedirectHistory';
const REDIRECT_HISTORY_WINDOW_MS = 5 * 60 * 1000;

// B2C embeds its own error code in the message, e.g. "AADB2C90118: The user has forgotten their password."
const B2C_ERROR_CODE_PATTERN = /AADB2C\d+/;

function getAuthErrorProperties(error: unknown): Record<string, string> {
  if (error == null) {
    return {};
  }

  if (error instanceof AuthError) {
    const b2cErrorCode = B2C_ERROR_CODE_PATTERN.exec(error.errorMessage ?? '')?.[0];
    return {
      errorName: error.name,
      errorCode: error.errorCode,
      subError: error.subError,
      correlationId: error.correlationId,
      errorMessage: error.errorMessage,
      ...(b2cErrorCode != null ? { b2cErrorCode } : {}),
    };
  }

  return {
    errorName: error instanceof Error ? error.name : typeof error,
    errorMessage: getErrorDetails(error),
  };
}

// Records this redirect in sessionStorage and returns how many forced redirects happened in the
// last few minutes (including this one), so a login loop shows up as a rising count.
function recordRedirect(reason: AuthRedirectReason): number {
  const now = Date.now();

  try {
    const stored = sessionStorage.getItem(REDIRECT_HISTORY_KEY);
    const history = stored != null ? (JSON.parse(stored) as RedirectHistoryEntry[]) : [];
    const recent = history.filter((entry) => now - entry.at < REDIRECT_HISTORY_WINDOW_MS);
    recent.push({ reason, at: now });
    sessionStorage.setItem(REDIRECT_HISTORY_KEY, JSON.stringify(recent));
    return recent.length;
  } catch {
    // sessionStorage can be unavailable (privacy mode, blocked storage); the count is best-effort.
    return 1;
  }
}

export function getRecentAuthRedirects(): RedirectHistoryEntry[] {
  try {
    const stored = sessionStorage.getItem(REDIRECT_HISTORY_KEY);
    const history = stored != null ? (JSON.parse(stored) as RedirectHistoryEntry[]) : [];
    return history.filter((entry) => Date.now() - entry.at < REDIRECT_HISTORY_WINDOW_MS);
  } catch {
    return [];
  }
}

// Call immediately before logoutRedirect / acquireTokenRedirect / login so the reason is sent
// before the page navigates away.
export function trackAuthRedirect(
  reason: AuthRedirectReason,
  details: AuthRedirectDetails = {}
): void {
  const { error, b2cId, ...extra } = details;
  const expected = EXPECTED_REASONS.has(reason);

  const properties: Record<string, string | number | boolean> = {
    reason,
    expected,
    route: window.location.pathname,
    recentRedirectCount: recordRedirect(reason),
    ...getAuthErrorProperties(error),
  };

  if (b2cId != null) {
    properties.b2cId = b2cId;
  }

  for (const [key, value] of Object.entries(extra)) {
    if (value == null) continue;
    properties[key] =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? value
        : getErrorDetails(value);
  }

  appInsights.trackEvent({ name: 'AuthRedirect', properties });

  if (error != null) {
    logError(`Auth redirect (${reason}).`, error, properties);
  } else if (!expected) {
    console.warn(`Auth redirect (${reason}).`, properties);
  }

  try {
    // Synchronous flush: the redirect that follows unloads the page and can drop queued telemetry.
    void appInsights.flush(false);
  } catch {
    // Telemetry may not be loaded (no connection string in local dev).
  }
}
